import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { StringFieldComponent } from '~shared/components/form/fields/field/string-field/string-field.component';
import { LoaderComponent } from '~shared/components/loader/loader.component';
import { LoaderModule } from '~shared/components/loader/loader.module';
import { PasswordResetService } from '~shared/services/api/general/password-reset.service';
import { PuToastrService } from '~shared/services/toastr/pu-toastr.service';
import { passwordValidator } from '~shared/validators/password.validator';
import { LienEvitementComponent } from '~shared/components/lien-evitement/lien-evitement.component';

@Component({
  selector: 'app-reset-password-token',
  imports: [
    TranslateModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    StringFieldComponent,
    RouterLink,
    LoaderModule,
    LienEvitementComponent,
  ],
  providers: [LoaderComponent],
  templateUrl: './reset-password-token.component.html',
  styleUrl: './reset-password-token.component.scss',
})
export class ResetPasswordTokenComponent implements OnInit {
  private passwordResetService = inject(PasswordResetService);
  private toastr = inject(PuToastrService);
  private route = inject(ActivatedRoute);
  protected loader = inject(LoaderComponent);
  protected token = this.route.snapshot.params['token'];
  isTokenValid: boolean;
  isPassReseted: boolean;
  submitted = false;

  formGroup = new FormGroup({
    password: new FormControl('', [
      Validators.required,
      passwordValidator(false),
    ]),
    confirmPassword: new FormControl('', [Validators.required]),
  });

  passwordRules = {
    minLength: false,
    uppercase: false,
    lowercase: false,
    numberOrSpecial: false,
  };

  strengthPercent = 0;
  strengthColor = 'red';
  constructor() {
    this.formGroup.get('password')?.valueChanges.subscribe((value) => {
      this.evaluatePassword(value || '');
    });
  }
  ngOnInit(): void {
    this.loader.setLoading(true);
    this.passwordResetService.validateToken(this.token).subscribe(
      (response) => {
        this.isTokenValid = response;
        this.loader.setLoading(false);
      },
      (error) => {
        this.toastr.showErrorMessage(error);
        this.loader.setLoading(false);
      }
    );
  }

  private evaluatePassword(password: string) {
    this.passwordRules.minLength = password.length >= 10;
    this.passwordRules.uppercase = /[A-Z]/.test(password);
    this.passwordRules.lowercase = /[a-z]/.test(password);
    this.passwordRules.numberOrSpecial =
      /[0-9!@#$%^&*()_+\-=[\]{};:"'\\|,.<>/?~]/.test(password);

    // Calcul du score (0–4)
    const passedRules = Object.values(this.passwordRules).filter(
      Boolean
    ).length;
    this.strengthPercent = (passedRules / 4) * 100;

    // Couleur dynamique
    if (this.strengthPercent <= 25) this.strengthColor = 'red';
    else if (this.strengthPercent <= 50) this.strengthColor = 'orange';
    else if (this.strengthPercent <= 75) this.strengthColor = 'gold';
    else this.strengthColor = 'green';
  }

  isDefined(variable: any): boolean {
    return variable !== undefined && variable !== null;
  }

  onSubmit() {
    this.submitted = true;
    if (!this.formGroup.valid) {
      return;
    }
    const password = this.formGroup.get('password')?.value;
    const confirmPassword = this.formGroup.get('confirmPassword')?.value;
    if (password !== confirmPassword) {
      this.formGroup.get('confirmPassword')?.setErrors({ mismatch: true });
      return;
    }
    const formData = new URLSearchParams();
    formData.append('token', this.token);
    formData.append('password', String(password));
    this.loader.setLoading(true);
    this.passwordResetService.resetPassword(formData).subscribe(
      () => {
        this.isPassReseted = true;
        this.loader.setLoading(false);
      },
      (error) => {
        this.loader.setLoading(false);
        // Accès prudent : `error.error` porte le CORPS de la réponse, qui est
        // `null` quand le serveur échoue sans en renvoyer. Le lire directement
        // faisait alors lever la gestion d'erreur elle-même, et l'utilisateur
        // n'obtenait ni message ni explication.
        // Statut lu d'abord sur la réponse, que `HttpErrorResponse` renseigne
        // toujours ; le corps ne le porte que si l'API l'y a mis. Le message,
        // lui, ne vient que du corps : sans lui, rien à afficher.
        const refuse = error?.status === 400 || error?.error?.status === 400;
        const message = error?.error?.message;
        if (refuse && message) {
          this.formGroup
            .get('confirmPassword')
            ?.setErrors({ form_error: message });
        }
      }
    );
  }
}
